import '../../GenericTable.css'
import { PageChangeEvent } from '../../Models/pageChangeEvent'
import { JSX, useState, useEffect, useRef } from 'react'
import PageIndexButton from './PageIndexButton'

// A cold page prefetch triggers a full (heavy) list request server-side, so a
// cursor sweeping across the pagination bar must not fire any: only prefetch
// after the pointer has rested on a control for this long.
const HOVER_PREFETCH_DELAY_MS = 400

interface PaginatorProps {
  onNewPage: (nextPageEvent: PageChangeEvent) => void;
  firstPage?: number;
  lastPage: number;
  initialIndex: number;
  // Optional cache warmer for a page the user is about to click (default no-op
  // so existing consumers are unaffected). May return the prefetch promise so
  // in-flight work can be tracked.
  onPrefetchPage?: (pageIndex: number) => void | Promise<void>;
}


function Paginator({ onNewPage, lastPage = 1, initialIndex = 1, firstPage = 1, onPrefetchPage = () => {} }: PaginatorProps): JSX.Element {
  const [currentPage, setCurrentPage] = useState(Number(initialIndex))
  let indexButtons: JSX.Element[]

  // Sync currentPage with initialIndex prop changes
  useEffect(() => {
    setCurrentPage(Number(initialIndex))
  }, [initialIndex])

  // Hover-prefetch state: one shared rest-timer (a single pointer can only
  // hover one control at a time) and a max of ONE in-flight prefetch, since
  // each cold prefetch costs heavy server work.
  const hoverTimer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined)
  const prefetchInFlight = useRef(false)

  useEffect(() => () => clearTimeout(hoverTimer.current), [])

  function handleHoverStart(index: number) {
    if (index === currentPage || index < firstPage || index > lastPage) {
      return
    }
    clearTimeout(hoverTimer.current)
    hoverTimer.current = setTimeout(() => {
      if (prefetchInFlight.current) {
        return
      }
      prefetchInFlight.current = true
      // prefetchQuery never rejects, but Promise.resolve guards a void return.
      // try/finally also clears the flag on a synchronous throw, so a single
      // bad handler can never permanently disable hover prefetching.
      try {
        Promise.resolve(onPrefetchPage(index)).finally(() => {
          prefetchInFlight.current = false
        })
      } catch {
        prefetchInFlight.current = false
      }
    }, HOVER_PREFETCH_DELAY_MS)
  }

  function handleHoverEnd() {
    clearTimeout(hoverTimer.current)
  }

  function goToPage(index: number) {
    setCurrentPage(index)
    onNewPage({ value: '' + index })
  }

  function onNextPage() {
    const nextPage = currentPage + 1
    if (nextPage > lastPage) {
      return
    }

    setCurrentPage(nextPage)
    onNewPage({ value: '' + nextPage })
  }

  function onPrevPage() {
    const prevPage = currentPage - 1
    if (prevPage < firstPage) {
      return
    }

    setCurrentPage(prevPage)
    onNewPage({ value: '' + prevPage })
  }

  indexButtons = buildPaginationButtonBar(lastPage, firstPage, currentPage, goToPage, handleHoverStart, handleHoverEnd)
  const disabled1 = currentPage === firstPage
  const disabled2 = currentPage === lastPage
  return (
    <div className="flex gap-2">
      <button
        className={'Paginator--nav-btn ' + (disabled1 ? '' : 'white-btn-hover')}
        disabled={disabled1}
        onClick={onPrevPage}
        onPointerEnter={() => handleHoverStart(currentPage - 1)}
        onPointerLeave={handleHoverEnd}
      >
        Prev
      </button>
      {indexButtons}
      <button
        className={'Paginator--nav-btn ' + (disabled2 ? '' : 'white-btn-hover')}
        disabled={disabled2}
        onClick={onNextPage}
        onPointerEnter={() => handleHoverStart(currentPage + 1)}
        onPointerLeave={handleHoverEnd}
      >
        Next
      </button>
    </div>
  )
}


function DotSeparator(index: number): JSX.Element {
  return <span className="w-8 tracking-widest mt-1 text-center cursor-default" key={index}>...</span>
}

function IndexButton(
  index: number, currentPage: number, onGoToPage: (index: number) => void, uniqueKey: number,
  onHoverStart: (index: number) => void, onHoverEnd: () => void
): JSX.Element {
  return <PageIndexButton
    key={uniqueKey}
    pageIndex={index}
    goToPage={() => onGoToPage(index)}
    active={currentPage === index}
    onHoverStart={() => onHoverStart(index)}
    onHoverEnd={onHoverEnd}
  />
}

// Fixed pagination logic - no overlapping conditions
function buildPaginationButtonBar(
  lastPage: number, firstPage: number, currentPage: number,
  onGoToPage: (index: number) => void,
  onHoverStart: (index: number) => void, onHoverEnd: () => void
): JSX.Element[] {
  const indexButtons: JSX.Element[] = []

  // Simple case: show all pages if 9 or fewer
  if (lastPage <= 9) {
    for (let i = firstPage; i <= lastPage; i++) {
      indexButtons.push(IndexButton(i, currentPage, onGoToPage, i, onHoverStart, onHoverEnd))
    }
    return indexButtons
  }

  // Complex case: smart pagination with ellipses
  const pages: (number | 'ellipsis')[] = []

  // Always show first page
  pages.push(firstPage)

  // Determine the range around current page
  let rangeStart = Math.max(firstPage + 1, currentPage - 2)
  let rangeEnd = Math.min(lastPage - 1, currentPage + 2)

  // Adjust range to show at least 5 middle pages when possible
  const rangeSize = rangeEnd - rangeStart + 1
  if (rangeSize < 5 && lastPage > 7) {
    if (rangeStart === firstPage + 1) {
      rangeEnd = Math.min(lastPage - 1, rangeStart + 4)
    } else if (rangeEnd === lastPage - 1) {
      rangeStart = Math.max(firstPage + 1, rangeEnd - 4)
    }
  }

  // Add ellipsis before range if needed
  if (rangeStart > firstPage + 1) {
    pages.push('ellipsis')
  }

  // Add the range around current page
  for (let i = rangeStart; i <= rangeEnd; i++) {
    pages.push(i)
  }

  // Add ellipsis after range if needed
  if (rangeEnd < lastPage - 1) {
    pages.push('ellipsis')
  }

  // Always show last page (if different from first)
  if (lastPage > firstPage) {
    pages.push(lastPage)
  }

  // Convert to JSX elements with unique keys
  let keyCounter = 0
  pages.forEach(page => {
    if (page === 'ellipsis') {
      indexButtons.push(DotSeparator(keyCounter++))
    } else {
      indexButtons.push(IndexButton(page, currentPage, onGoToPage, keyCounter++, onHoverStart, onHoverEnd))
    }
  })

  return indexButtons
}

export default Paginator
